/**
 * The localStorage key `backend/api/api.ts` reads to build the `Authorization:
 * Bearer …` header. It is the only signal of a live session available
 * synchronously at boot, before any saga has run or `/me` has resolved.
 */
const TOKEN_KEY = "token";

/**
 * The key the rest of the app treats as "signed in" — App/index.tsx:99 gates its
 * `getUser()` boot call on it, as do privateRoute, permissionRoute, roleRoute,
 * AppBar and AuthLayout. Written only by the three login paths in authSaga, and
 * removed by logout, by AccountSuspendedPage, and — crucially — by the 401 arm of
 * the response interceptor in backend/api/api.ts.
 */
const USER_KEY = "user";

/**
 * Whether there is a session worth spending a request on.
 *
 * Boot-time background work must not fire without one. `/login` is served by the
 * same router root that mounts the sync engine, so a logged-out visitor would
 * otherwise issue an authenticated-looking request, take a 401, and be bounced by
 * the response interceptor's `window.location.replace("/login")` — which remounts
 * the router and starts the whole thing again. That is an unbreakable loop on the
 * one page the user needs in order to recover.
 *
 * BOTH keys are required, and the second one is what actually breaks the loop.
 * A token that is merely *present* proves nothing: the 401 interceptor never
 * removes `token`, and `offline_enabled` is only cleared on a clean 204 logout, so
 * a revoked, expired or DB-reset token leaves a token-only gate passing forever.
 * `user` is removed by that same 401 arm, so the first bounce closes the gate and
 * the second boot goes quiet. This is exactly why the rest of the app is immune —
 * it has always gated on `user`, never on `token`.
 *
 * No false negatives: every authSaga path that writes a token for a real session
 * (`loginUser`, `regUser`, `loginWithToken`) writes `user` in the same block. The
 * one path that writes a token alone is `getToken`, the XSRF bootstrap, which by
 * definition is not a session.
 *
 * `updateToken()` writes an empty string when the auth cookie is gone, so an
 * empty value has to count as "no session", not just a missing key.
 */
export const hasSession = (): boolean => {
  try {
    return Boolean(
      window.localStorage.getItem(TOKEN_KEY) && window.localStorage.getItem(USER_KEY)
    );
  } catch {
    // localStorage throws in some locked-down/private contexts — fail closed, the
    // same way isOfflineFeatureEnabled() does.
    return false;
  }
};
